import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AddressAccessControlSelectorDto } from '@shared/src/components/accesscontrol/address/shared.address.accesscontrol.selector.component';
import { AccessControlController } from '@shared/src/controllers/accesscontrol/accesscontrol.controller';
import { HLong } from '@shared/src/datatypes/HLong';
import { HString } from '@shared/src/datatypes/HString';
import { ForkliftDto } from '@shared/src/dtos/accesscontrol/forklift/ForkliftDto';
import { ForkliftFilterDto } from '@shared/src/dtos/accesscontrol/forklift/ForkliftFilterDto';
import { OptionDto } from '@shared/src/dtos/maintenance/option/OptionDto';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'shared-forklift',
    templateUrl: './shared.forklift.component.html',
    styleUrls: ['./shared.forklift.component.scss']
})
export class SharedForkliftComponent implements OnInit, OnDestroy {

    public static PARAMETERACCESSCONTROL = 'accesscontrolid';
    public static PARAMETERADDRESS = 'addressid';
    public static PARAMETERGATE = 'gateid';

    public addressAccessControl: AddressAccessControlSelectorDto;
    public filter: ForkliftFilterDto;
    public value: ForkliftDto;
    public isRightOpen: boolean = false;
    public selectedGateId: string = '';

    private filterChange$ = new Subject<ForkliftFilterDto>();
    private destroy$ = new Subject<void>();

    constructor(private activatedRoute: ActivatedRoute, private router: Router,
        private accessControlController: AccessControlController) { }

    ngOnInit(): void {
        this.filterChange$.pipe(
            debounceTime(1000),
            distinctUntilChanged((a, b) => ForkliftFilterDto.equals(a, b)),
            takeUntil(this.destroy$)
        ).subscribe(filter => this.loadData(filter));

        this.loadParameters();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadParameters() {
        const params = this.activatedRoute.snapshot.queryParamMap;
        const rawAC = params.get(SharedForkliftComponent.PARAMETERACCESSCONTROL);
        const rawAddr = params.get(SharedForkliftComponent.PARAMETERADDRESS);
        const rawGate = params.get(SharedForkliftComponent.PARAMETERGATE);
        const accessControlId = HString.isNullOrNullString(rawAC) ? null : +rawAC;
        const addressId = HString.isNullOrNullString(rawAddr) ? null : +rawAddr;
        const gateId = HString.isNullOrNullString(rawGate) ? null : +rawGate;
        this.addressAccessControl = AddressAccessControlSelectorDto.buildStandard('forklift');
        this.addressAccessControl.accessControlId = accessControlId;
        this.addressAccessControl.addressId = addressId;
        this.selectedGateId = gateId ? gateId.toString() : '';
        this.updateFilter(accessControlId, addressId, gateId);
    }

    onChangeAddressAccessControl(value: AddressAccessControlSelectorDto) {
        this.addressAccessControl = value;
        const accessControlId = value?.accessControlId ?? null;
        const addressId = value?.addressId ?? null;
        this.selectedGateId = '';
        this.updateFilter(accessControlId, addressId, null);
        this.setParametersToURL();
    }

    private updateFilter(accessControlId: number, addressId: number, gateId: number) {
        this.filter = ForkliftFilterDto.build(accessControlId, addressId, gateId);
        this.filterChange$.next(this.filter);
    }

    private loadData(filter: ForkliftFilterDto) {
        this.accessControlController.getForklift(filter).subscribe(value => {
            this.value = value;
        });
    }

    setParametersToURL() {
        const object: any = {};
        if (!HLong.isNullOrNullLong(this.addressAccessControl?.accessControlId))
            object[SharedForkliftComponent.PARAMETERACCESSCONTROL] = this.addressAccessControl.accessControlId;
        if (!HLong.isNullOrNullLong(this.addressAccessControl?.addressId))
            object[SharedForkliftComponent.PARAMETERADDRESS] = this.addressAccessControl.addressId;
        if (!HString.isNullOrNullString(this.selectedGateId))
            object[SharedForkliftComponent.PARAMETERGATE] = this.selectedGateId;

        this.router.navigate(['.', {}], {
            relativeTo: this.activatedRoute,
            queryParams: object,
            queryParamsHandling: ''
        });
    }

    onClickGateOption(option: OptionDto) {
        this.selectedGateId = option?.optionId ?? '';
        const gateId = HString.isNullOrNullString(this.selectedGateId) ? null : +this.selectedGateId;
        this.filter = ForkliftFilterDto.build(this.filter?.accessControlId, this.filter?.addressId, gateId);
        this.filterChange$.next(this.filter);
        this.setParametersToURL();
    }
    public pretty(value: any): string {
        return JSON.stringify(value);
    }
}
